/**
 * Outstanding Service
 * Handles outstanding invoices and payments API calls
 */

import { apiClient } from "../client";
import type {
  OutstandingInvoice,
  OutstandingListResponse,
  OutstandingSummary,
  FilterParams,
  OutstandingApiResponse,
  OutstandingBillDetail,
} from "../types";

export const outstandingService = {
  /**
   * Get list of outstanding invoices
   * Endpoint: DirectorModule/outstanding?firmId={firmId}
   */
  async getOutstanding(params?: { firmId?: number }): Promise<OutstandingApiResponse[]> {
    await apiClient.loadConfig();
    const response = await apiClient.get<OutstandingApiResponse[]>("DirectorModule/outstanding", params);
    // Handle both direct array response and wrapped response
    const data = response.data;
    return Array.isArray(data) ? data : [];
  },

  /**
   * Get outstanding summary
   */
  async getOutstandingSummary(params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<OutstandingSummary> {
    const response = await apiClient.get<OutstandingSummary>("/outstanding/summary", params);
    return response.data;
  },

  /**
   * Get outstanding by customer
   */
  async getOutstandingByCustomer(customerId: string): Promise<OutstandingInvoice[]> {
    const response = await apiClient.get<OutstandingInvoice[]>(
      `/outstanding/customer/${customerId}`
    );
    return response.data;
  },

  /**
   * Get aging analysis
   */
  async getAgingAnalysis(): Promise<{
    "<15": number;
    "15-30": number;
    "30-60": number;
    "60+": number;
  }> {
    const response = await apiClient.get<{
      "<15": number;
      "15-30": number;
      "30-60": number;
      "60+": number;
    }>("/outstanding/aging");
    return response.data;
  },

  /**
   * Get bill-wise outstanding details for a party
   * Endpoint: salesman/outstanding?firmId={firmId}&partyCode={partyCode}&mode=0
   */
  async getOutstandingBillDetails(params: {
    firmId: number;
    partyCode: string;
    mode?: number;
  }): Promise<OutstandingBillDetail[]> {
    await apiClient.loadConfig();
    const response = await apiClient.get<OutstandingBillDetail[]>("salesman/outstanding", {
      firmId: params.firmId,
      partyCode: params.partyCode,
      mode: params.mode ?? 0,
    });
    // Handle both direct array response and wrapped response
    const data = response.data;
    return Array.isArray(data) ? data : [];
  },

  /**
   * Get WhatsApp message text for outstanding details
   * Endpoint: Common/whatsapp-message?firmId={firmId}&orderNo={orderNo}&mode={mode}&prefix={prefix}
   */
  async getWhatsAppMessage(params: {
    firmId: number;
    orderNo?: number;
    mode: number;
    prefix: string;
  }): Promise<{ whatsappText: string }> {
    await apiClient.loadConfig();

    const queryParams = new URLSearchParams({
      firmId: String(params.firmId),
      orderNo: String(params.orderNo ?? 0),
      mode: String(params.mode),
      prefix: params.prefix,
    });

    const endpoint = `Common/whatsapp-message?${queryParams.toString()}`;
    const response = await apiClient.get<{ whatsappText: string }>(endpoint);
    return response.data;
  },

  /**
   * Send the outstanding statement PDF on WhatsApp
   * POST common/send-outstanding-file?firmId={firmId}&partyCode={partyCode}&mode={mode}
   */
  async sendOutstandingFile(params: {
    firmId: number;
    partyCode: string;
    mode?: number;
  }): Promise<{ success: boolean; message: string }> {
    await apiClient.loadConfig();

    const queryParams = new URLSearchParams({
      firmId: String(params.firmId),
      partyCode: params.partyCode,
      mode: String(params.mode ?? 4),
    });

    const response = await apiClient.post<{ success?: boolean; message?: string }>(
      `common/send-outstanding-file?${queryParams.toString()}`
    );

    const payload = response.data;
    return {
      success: response.success !== false && payload?.success !== false,
      message:
        response.message ||
        payload?.message ||
        "Outstanding file sent successfully.",
    };
  },
};

